// Utilities for CTA links that embed URL query-param placeholders like {{TokenName}}.
// A token placeholder in a link's query string is replaced at click time with the
// matching query param from the current page URL. A param whose token has no match
// in the source URL is dropped entirely (so no empty/dangling params are forwarded).

const TOKEN_REGEX = /\{\{\s*([A-Za-z0-9_]+)\s*\}\}/g;

/**
 * Return every token name referenced anywhere in a link.
 * e.g. "/x?a={{Foo}}&b={{Bar}}" -> ["Foo", "Bar"]. Plain/empty links -> [].
 */
export function getTemplateTokens(link = '') {
  const tokens = [];
  if (!link) return tokens;
  TOKEN_REGEX.lastIndex = 0;
  let match = TOKEN_REGEX.exec(link);
  while (match !== null) {
    tokens.push(match[1]);
    match = TOKEN_REGEX.exec(link);
  }
  return tokens;
}

/**
 * Resolve {{Token}} placeholders in a link's query string against sourceParams
 * (a URLSearchParams from the current page URL). For each query param whose value
 * contains one or more tokens: every token is substituted with its source value;
 * if ANY referenced token is missing from the source, the whole param is dropped.
 * Static params and the path/hash are left untouched. Returns a clean link with no
 * dangling "?". Links without tokens are returned unchanged.
 */
export function resolveLinkTemplate(link, sourceParams) {
  if (!link || link.indexOf('{{') === -1) return link;

  const hashIndex = link.indexOf('#');
  const hash = hashIndex === -1 ? '' : link.slice(hashIndex);
  const withoutHash = hashIndex === -1 ? link : link.slice(0, hashIndex);

  const queryIndex = withoutHash.indexOf('?');
  // Tokens are only supported inside a query string; otherwise leave link untouched.
  if (queryIndex === -1) return link;

  const base = withoutHash.slice(0, queryIndex);
  const queryString = withoutHash.slice(queryIndex + 1);

  const resolvedPairs = [];
  queryString.split('&').forEach((pair) => {
    if (!pair) return;
    const eqIndex = pair.indexOf('=');
    const key = eqIndex === -1 ? pair : pair.slice(0, eqIndex);
    const value = eqIndex === -1 ? '' : pair.slice(eqIndex + 1);

    const tokens = getTemplateTokens(value);
    if (tokens.length === 0) {
      resolvedPairs.push(pair);
      return;
    }

    // Drop the param if any referenced token has no value in the source URL.
    const hasMissing = tokens.some(
      (token) => !sourceParams || sourceParams.get(token) === null
    );
    if (hasMissing) return;

    let resolvedValue = value;
    tokens.forEach((token) => {
      resolvedValue = resolvedValue.replace(
        new RegExp(`\\{\\{\\s*${token}\\s*\\}\\}`, 'g'),
        encodeURIComponent(sourceParams.get(token))
      );
    });
    resolvedPairs.push(`${key}=${resolvedValue}`);
  });

  const resolvedQuery = resolvedPairs.join('&');
  return `${base}${resolvedQuery ? `?${resolvedQuery}` : ''}${hash}`;
}
